import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Download, CheckCircle2, BookOpen, BarChart2, Users, Cpu, TrendingUp, Map, FileText } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "https://revops-backend-production.up.railway.app";
const PDF_URL = "/RYVR_RevOps_WP_2026.pdf";

const CHAPTERS = [
  { icon: FileText,   color: "#7C3AED", title: "Executive Summary",                   desc: "75% of high-growth firms adopt RevOps by 2026. Why now, and what drives the 3% revenue uplift." },
  { icon: BarChart2,  color: "#00B4A0", title: "How RevOps Scope Differs",             desc: "B2B, B2C, and D2C each have distinct motion, data assets, and highest-friction steps." },
  { icon: BookOpen,   color: "#F59E0B", title: "RevOps Comparison Matrix",             desc: "Side-by-side: primary buyer motion, natural RevOps centre, core data asset, main failure mode." },
  { icon: Users,      color: "#7C3AED", title: "Operating Model & Team Roles",         desc: "Hybrid org structure, team sizing ratios (1:12-15 AE seats), and highest-impact functions." },
  { icon: Cpu,        color: "#00B4A0", title: "Technology Stack & AI Readiness",      desc: "Five-part AI readiness framework. Why 92% plan to invest but only 1% call themselves mature." },
  { icon: TrendingUp, color: "#F59E0B", title: "Market Trends & Psychology",           desc: "Omnichannel B2B, data consolidation, pricing centrality — and the human biases that stall RevOps." },
  { icon: BarChart2,  color: "#EF4444", title: "KPIs & Benchmarks",                   desc: "Model-specific benchmarks: NRR, CAC:LTV, funnel conversion, churn, forecast accuracy." },
  { icon: Map,        color: "#7C3AED", title: "Implementation Roadmap",               desc: "Four phases from Day 1 diagnostic to AI scaling at Day 181+. Quick wins under 90 days." },
];

const ROLES = ["Founder / CEO", "CMO / Marketing Head", "VP / Head of Sales", "RevOps Lead", "Consultant / Advisor", "Other"];
const CO_TYPES = ["B2B (Business-to-Business)", "B2C (Business-to-Consumer)", "D2C (Direct-to-Consumer)", "Mixed / Platform"];

export default function WhitepaperPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", company_type: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      setErr("Please fill in all required fields.");
      return;
    }
    setErr("");
    setStatus("loading");
    try {
      const res = await fetch(`${API_BASE}/api/whitepaper-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error");
      setStatus("success");
    } catch {
      setStatus("error");
      setErr("Something went wrong. Please try again or email jigesh@ryvr.in.");
    }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="focus:outline-none">
            <img src="/ryvr-logo.png" alt="RYVR" className="h-8 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block">
              Consultant login
            </button>
            <button
              onClick={() => navigate("/assess")}
              className="px-4 py-2 rounded-lg bg-[#00B4A0] hover:bg-[#009383] text-white text-sm font-medium transition-colors"
            >
              Free assessment
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-0 bg-[#0D0820]">
        <div className="max-w-6xl mx-auto px-6 pt-10 pb-0">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#A78BFA] text-xs font-mono mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" />
            New · State of RevOps 2026
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: headline + stats */}
            <div className="pb-12">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                State of<br />
                <span className="text-[#A78BFA]">RevOps</span> 2026
              </h1>
              <p className="text-gray-400 text-base leading-relaxed mb-8 max-w-lg">
                A comprehensive guide to Revenue Operations strategy, team design, AI readiness,
                and implementation across business models. 10 pages. Free.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { val: "75%", label: "high-growth firms will use RevOps by 2026", color: "#A78BFA" },
                  { val: "79%", label: "already have a formal RevOps function", color: "#34D399" },
                  { val: "3%",  label: "revenue uplift reported by early adopters", color: "#FCD34D" },
                ].map(({ val, label, color }) => (
                  <div key={val} className="p-4 rounded-xl border border-white/10 bg-white/5">
                    <div className="text-3xl font-bold mb-1" style={{ color }}>{val}</div>
                    <div className="text-[11px] text-gray-400 leading-snug">{label}</div>
                  </div>
                ))}
              </div>

              {/* Trust line */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                {["RevOps Strategy", "AI Readiness", "B2B / B2C / D2C", "KPI Benchmarks", "Implementation Roadmap"].map(t => (
                  <span key={t} className="flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#34D399]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: form card (bleeds into white below) */}
            <div className="lg:pt-2 pb-0">
              <div className="bg-white rounded-t-2xl shadow-2xl p-8 border border-gray-100">
                {status === "success" ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 rounded-full bg-[#00B4A0]/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-7 h-7 text-[#00B4A0]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">You're all set</h3>
                    <p className="text-gray-500 text-sm mb-6">Download your copy of the State of RevOps 2026 report below.</p>
                    <a
                      href={PDF_URL}
                      download="RYVR_RevOps_WP_2026.pdf"
                      className="flex items-center justify-center gap-2 w-full h-12 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Report (PDF)
                    </a>
                    <p className="text-xs text-gray-400 mt-4">
                      We've noted your details and will be in touch if relevant. No spam.
                    </p>
                    <div className="mt-6 pt-5 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-3">Want to benchmark your RevOps against these findings?</p>
                      <button
                        onClick={() => navigate("/assess")}
                        className="w-full flex items-center justify-center gap-2 h-10 px-6 border border-[#00B4A0] text-[#00B4A0] hover:bg-[#00B4A0] hover:text-white font-medium rounded-xl transition-colors text-sm"
                      >
                        Take the free assessment <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-gray-900 mb-1">Download the report</h2>
                    <p className="text-sm text-gray-500 mb-6">Free. No paywall. We just want to know who's reading.</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Full name *</label>
                          <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Jane Smith"
                            required
                            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Work email *</label>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="jane@company.com"
                            required
                            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Company *</label>
                        <input
                          name="company"
                          value={form.company}
                          onChange={handleChange}
                          placeholder="Acme Corp"
                          required
                          className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Your role</label>
                          <select
                            name="role"
                            value={form.role}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
                          >
                            <option value="">Select…</option>
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Business model</label>
                          <select
                            name="company_type"
                            value={form.company_type}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
                          >
                            <option value="">Select…</option>
                            {CO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>

                      {err && <p className="text-xs text-red-500">{err}</p>}

                      <button
                        type="submit"
                        disabled={status === "loading"}
                        className="w-full h-11 flex items-center justify-center gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-60 text-white font-semibold rounded-xl transition-colors text-sm"
                      >
                        {status === "loading" ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Submitting…
                          </span>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Get the whitepaper
                          </>
                        )}
                      </button>
                      <p className="text-[11px] text-gray-400 text-center">
                        No spam. We'll only reach out if it's directly relevant to your work.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-mono text-[#7C3AED] uppercase tracking-wider">Inside the report</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">8 sections. One complete picture.</h2>
            <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">
              From org design to AI readiness, benchmarks to implementation roadmap — built for RevOps practitioners and commercial leaders.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CHAPTERS.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="p-5 rounded-2xl border border-gray-100 bg-white shadow-sm hover:border-gray-200 hover:shadow-md transition-all">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4" style={{ background: `${color}10` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-6 bg-gray-50 border-t border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Ready to benchmark your own RevOps?
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Take RYVR's free 10-minute assessment and score your Brand, Marketing, and Revenue Operations against benchmarks for your company type.
          </p>
          <button
            onClick={() => navigate("/assess")}
            className="inline-flex items-center gap-2 h-12 px-8 bg-[#00B4A0] hover:bg-[#009383] text-white font-semibold rounded-xl transition-colors"
          >
            Start the free assessment <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-gray-400 mt-3">No login required · 10 minutes · Free</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <span>© {new Date().getFullYear()} RYVR · revops.ryvr.in</span>
          <a href="mailto:jigesh@ryvr.in" className="hover:text-gray-700 transition-colors">jigesh@ryvr.in</a>
        </div>
      </footer>
    </div>
  );
}
